import { isPlatformBrowser } from '@angular/common';
import {
  DestroyRef,
  Injectable,
  PLATFORM_ID,
  inject,
  signal,
} from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';

/** Pixels of vertical overlap before treating the keyboard as open (avoid flicker). */
const VIEWPORT_THRESHOLD_SHOW = 100;
/** Hysteresis: hide only when overlap drops below this. */
const VIEWPORT_THRESHOLD_HIDE = 60;

@Injectable({ providedIn: 'root' })
export class KeyboardUiService {
  private readonly platformId = inject(PLATFORM_ID);

  readonly isKeyboardOpen = signal(false);

  attach(destroyRef: DestroyRef): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (Capacitor.isNativePlatform()) {
      void this.#attachCapacitorKeyboard(destroyRef);
    } else {
      this.#attachVisualViewport(destroyRef);
    }
  }

  async #attachCapacitorKeyboard(destroyRef: DestroyRef): Promise<void> {
    try {
      const showHandle = await Keyboard.addListener('keyboardWillShow', () => {
        this.isKeyboardOpen.set(true);
      });
      const hideHandle = await Keyboard.addListener('keyboardWillHide', () => {
        this.isKeyboardOpen.set(false);
      });
      destroyRef.onDestroy(() => {
        void showHandle.remove();
        void hideHandle.remove();
      });
    } catch {
      this.#attachVisualViewport(destroyRef);
    }
  }

  #attachVisualViewport(destroyRef: DestroyRef): void {
    const vv = globalThis.visualViewport;
    if (!vv) {
      return;
    }

    const update = (): void => {
      const overlap =
        globalThis.innerHeight - vv.height - vv.offsetTop;
      if (overlap > VIEWPORT_THRESHOLD_SHOW) {
        this.isKeyboardOpen.set(true);
      } else if (overlap < VIEWPORT_THRESHOLD_HIDE) {
        this.isKeyboardOpen.set(false);
      }
    };

    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    destroyRef.onDestroy(() => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    });
  }
}
